import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Link2, Mail, Monitor, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import type {
  GameAccountDto,
  GameAccountSecretField,
  RevealGameAccountInput,
  RevealedSecret,
} from '@gc/shared';

import { FilterDisclosure } from '@/components/layout/FilterDisclosure';
import { api } from '@/lib/api';
import { useAuth } from '../auth/useAuth';
import { AccountVaultCard } from './AccountVaultCard';
import { GameAccountDrawer } from './GameAccountDrawer';
import { GameAccountsAssignDialog } from './GameAccountsAssignDialog';
import { GameAccountsLinkDialog } from './GameAccountsLinkDialog';
import { ghostPillClass, inputClass, pillClass } from './controls';
import {
  NO_HOME_DEVICE,
  filterByHomeDevice,
  homeDeviceFilterOptions,
  idsWhoseNameContains,
} from './gameAccountBulk';
import { gameAccountControlsFor, isOwnerOnlyRefusal, revealKey } from './gameAccountForm';
import { toFieldErrors, useResource } from './useSettings';

/** How long a revealed secret stays on screen before it is masked again. */
const REVEAL_VISIBLE_MS = 30_000;

/**
 * The list header's two filter selects, sized like the other list-header
 * filters (Products, Customers, Bills, the challenges' game filter) rather than
 * as `${inputClass} w-auto`: both set width and Tailwind emits `.w-auto` before
 * `.w-full`, so inputClass's w-full won and each select took a header row of
 * its own (review, 2026-09-23). max-w-full keeps a long company or PC name
 * from widening one past a phone; on a phone the Filters fold still stacks
 * them one per line.
 */
const filterSelectClass =
  'max-w-full rounded-control border border-subtle bg-input px-2.5 py-1.5 text-sm text-content outline-none focus:border-primary';

/**
 * inputClass without its `w-full`, for the two header text boxes that set
 * their own width: the same cascade as the selects above — `.w-56` and
 * `.w-48` are emitted before `.w-full`, so appending one to inputClass never
 * took effect and the "Name contains" box spanned the whole bulk bar.
 */
const sizedInputClass = inputClass.replace(/\bw-full\b\s*/, '');

/**
 * Game Accounts (SPEC §6.5).
 *
 * Columns: Account Name · Username · Email · Company · Games (count) · Made
 * for PC · Edit · Delete. The spec's Active Device column went with the
 * borrow system (owner, 2026-10-05): nothing leases an account any more, so
 * it could only ever say "Free" — or name a PC from a lease nothing will
 * release. Made for PC answers "which PC is it on" now: it is the PC staff
 * keep the account signed in on.
 *
 * Passwords are NEVER in this list — the API does not return them. Revealing
 * one is an explicit, audited, per-account, per-secret action.
 *
 * Owner-only (owner, 2026-09-08): an account now carries its mailbox's
 * password too, and the PCs' Accounts app hands every secret to whoever
 * knows the vault password. Adding, editing, deleting and revealing are
 * therefore the owner's alone — the API refuses everyone else with
 * `owner_only`, and this screen keeps those controls off for them rather
 * than offering a button that can only fail. Staff still see the list: it
 * carries no secret, and which account is on which PC is their business.
 *
 * Made for PC (owner, 2026-09-23): each account can name the PC it was set
 * up on, which that PC's Accounts app lists under "This PC's credentials".
 * The list shows it and filters by it; the owner gets two bulk tools —
 * tick accounts and "Add game…", and "Assign to PCs by number", a preview
 * read off the numbers in the account names that nothing is written from
 * until it is confirmed.
 */
export function GameAccountsPage() {
  const { t } = useTranslation();
  const { employee } = useAuth();
  const controls = gameAccountControlsFor(employee);

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [company, setCompany] = useState('');
  const [editing, setEditing] = useState<GameAccountDto | null>(null);
  const [creating, setCreating] = useState(false);
  // Keyed by `revealKey(id, field)`: the two secrets of one account are shown
  // and re-masked independently. Component state only — never persisted.
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const maskTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // '' = every PC; NO_HOME_DEVICE = venue-wide accounts only; else a device id.
  const [pcFilter, setPcFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [nameContains, setNameContains] = useState('');
  const [linking, setLinking] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Leaving the screen ends every reveal clock; the values die with the state.
  useEffect(() => {
    const timers = maskTimers.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const params = new URLSearchParams();
  if (debounced) params.set('search', debounced);
  if (company) params.set('company', company);
  const query = params.toString() ? `?${params.toString()}` : '';

  const { data, loading, reload } = useResource<GameAccountDto[]>(
    `/settings/game-accounts${query}`,
  );

  const allAccounts = data ?? [];
  const companies = [...new Set(allAccounts.map((account) => account.company))].sort();
  const pcOptions = homeDeviceFilterOptions(allAccounts);
  // A PC that a new search left with no accounts drops out of the options;
  // filtering on it anyway would show an empty list under "All PCs".
  const shownPcFilter =
    pcFilter === '' || pcFilter === NO_HOME_DEVICE || pcOptions.some((o) => o.id === pcFilter)
      ? pcFilter
      : '';
  const accounts = filterByHomeDevice(allAccounts, shownPcFilter);
  // A tick on a row the filters now hide is not acted on: what "Add game…"
  // touches is exactly what the owner can see ticked.
  const selectedAccounts = accounts.filter((account) => selected.has(account.id));
  const allShownSelected = accounts.length > 0 && selectedAccounts.length === accounts.length;

  function toggleSelected(id: string, checked: boolean): void {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function selectAllShown(checked: boolean): void {
    setSelected(checked ? new Set(accounts.map((account) => account.id)) : new Set());
  }

  function selectMatching(): void {
    const ids = idsWhoseNameContains(accounts, nameContains);
    setSelected((current) => new Set([...current, ...ids]));
    setNotice(t('gameAccountTools.selectedMatching', { count: ids.length }));
  }

  function afterBulk(message: string): void {
    setLinking(false);
    setAssigning(false);
    setSelected(new Set());
    setNotice(message);
    void reload();
  }

  function renderPc(account: GameAccountDto): ReactNode {
    return (
      account.homeDeviceName ?? (
        <span className="text-content-muted">{t('gameAccountTools.venueWideShort')}</span>
      )
    );
  }

  function describeFailure(caught: unknown, fallback: string): string | null {
    if (isOwnerOnlyRefusal(caught)) return t('settings.gameAccounts.ownerOnly');
    return toFieldErrors(caught, fallback)[0]?.message ?? null;
  }

  async function reveal(account: GameAccountDto, field: GameAccountSecretField): Promise<void> {
    setError(null);
    const key = revealKey(account.id, field);
    try {
      const body: RevealGameAccountInput = { field };
      const secret = await api.post<RevealedSecret>(
        `/settings/game-accounts/${account.id}/reveal-password`,
        body,
      );
      setRevealed((current) => ({ ...current, [key]: secret.value }));
      // Auto-hide: a revealed password left on screen is the failure mode this
      // whole flow exists to control.
      const previous = maskTimers.current.get(key);
      if (previous) clearTimeout(previous);
      maskTimers.current.set(
        key,
        setTimeout(() => {
          maskTimers.current.delete(key);
          setRevealed((current) => {
            const next = { ...current };
            delete next[key];
            return next;
          });
        }, REVEAL_VISIBLE_MS),
      );
    } catch (caught) {
      setError(describeFailure(caught, t('settings.gameAccounts.revealFailed')));
    }
  }

  async function handleDelete(account: GameAccountDto): Promise<void> {
    if (!window.confirm(t('settings.gameAccounts.confirmDelete', { name: account.accountName })))
      return;
    setError(null);
    try {
      await api.delete(`/settings/game-accounts/${account.id}`);
      await reload();
    } catch (caught) {
      setError(describeFailure(caught, t('settings.gameAccounts.deleteFailed')));
    }
  }

  /**
   * One secret's control: the masked value's reveal button, or the value
   * itself for its thirty seconds. The mailbox one only exists when a mailbox
   * password is stored — a reveal of nothing is not offered.
   *
   * A plain render function, not a component: a component declared inside
   * the page is a new type on every render, so React would unmount and
   * remount every reveal button each time the search box changes or a secret
   * appears — and the button the owner just pressed would lose its focus.
   */
  function renderSecret(
    account: GameAccountDto,
    field: GameAccountSecretField,
    size: 'row' | 'card',
  ): ReactNode {
    if (field === 'emailPassword' && !account.hasEmailPassword) return null;
    const value = revealed[revealKey(account.id, field)];
    if (value !== undefined) {
      return (
        <code className="select-all text-xs text-content" dir="ltr">
          {value}
        </code>
      );
    }
    const label =
      field === 'password'
        ? t('settings.gameAccounts.reveal')
        : t('settings.gameAccounts.revealMailboxPassword');
    const Icon = field === 'password' ? Eye : Mail;
    return (
      <button
        type="button"
        onClick={() => void reveal(account, field)}
        title={label}
        aria-label={label}
        className={`grid place-items-center rounded-full text-content-secondary hover:bg-hover ${
          size === 'row' ? 'h-8 w-8' : 'h-10 w-10'
        }`}
      >
        <Icon className="h-4 w-4" aria-hidden />
      </button>
    );
  }

  // Name, username, email, company, games, Made for PC — plus the checkbox,
  // secrets, edit and delete for the owner.
  const columnCount = controls.canManage ? 10 : 6;

  return (
    <div className="p-6">
      <header className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-content">{t('settings.nav.gameAccounts')}</h1>

        <div className="relative">
          <Search
            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-content-muted"
            style={{ insetInlineStart: '0.6rem' }}
            aria-hidden
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('settings.gameAccounts.searchPlaceholder')}
            className={`${sizedInputClass} w-56 max-w-full ps-8`}
          />
        </div>

        {/* Phone: the two narrowing selects fold behind Filters; the search
            stays out. Desktop: the same row. */}
        <FilterDisclosure
          storageKey="settings.gameAccounts.filters"
          badge={(company ? 1 : 0) + (shownPcFilter ? 1 : 0)}
        >
          <select
            value={company}
            onChange={(event) => setCompany(event.target.value)}
            className={filterSelectClass}
          >
            <option value="">{t('settings.gameAccounts.allCompanies')}</option>
            {companies.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <select
            value={shownPcFilter}
            onChange={(event) => setPcFilter(event.target.value)}
            aria-label={t('gameAccountTools.pcFilter')}
            className={filterSelectClass}
            data-pc-filter
          >
            <option value="">{t('gameAccountTools.allPcs')}</option>
            <option value={NO_HOME_DEVICE}>{t('gameAccountTools.venueWideOnly')}</option>
            {pcOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </FilterDisclosure>

        <div className="flex-1" />

        {controls.canManage ? (
          <>
            <button type="button" onClick={() => setAssigning(true)} className={ghostPillClass}>
              <span className="inline-flex items-center gap-1">
                <Monitor className="h-4 w-4" aria-hidden />
                {t('gameAccountTools.assignByNumber')}
              </span>
            </button>
            <button type="button" onClick={() => setCreating(true)} className={pillClass}>
              <span className="inline-flex items-center gap-1">
                <Plus className="h-4 w-4" aria-hidden />
                {t('settings.gameAccounts.addNew')}
              </span>
            </button>
          </>
        ) : null}
      </header>

      {controls.canManage ? (
        <div
          className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-subtle bg-raised p-3"
          data-bulk-bar
        >
          <input
            value={nameContains}
            onChange={(event) => setNameContains(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                selectMatching();
              }
            }}
            placeholder={t('gameAccountTools.nameContains')}
            aria-label={t('gameAccountTools.nameContains')}
            className={`${sizedInputClass} w-48 max-w-full`}
          />
          <button
            type="button"
            onClick={selectMatching}
            disabled={!nameContains.trim()}
            className={ghostPillClass}
          >
            {t('gameAccountTools.selectMatching')}
          </button>
          <div className="flex-1" />
          <span className="text-sm text-content-secondary tabular">
            {t('gameAccountTools.selected', { count: selectedAccounts.length })}
          </span>
          {selectedAccounts.length > 0 ? (
            <button type="button" onClick={() => setSelected(new Set())} className={ghostPillClass}>
              {t('gameAccountTools.clearSelection')}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setLinking(true)}
            disabled={selectedAccounts.length === 0}
            className={pillClass}
          >
            <span className="inline-flex items-center gap-1">
              <Link2 className="h-4 w-4" aria-hidden />
              {t('gameAccountTools.addGame')}
            </span>
          </button>
        </div>
      ) : null}

      {notice ? (
        <p role="status" className="mb-3 text-sm text-success">
          {notice}
        </p>
      ) : null}

      {controls.showsOwnerOnlyNote ? (
        <p className="mb-3 text-xs text-content-muted" data-owner-only-note>
          {t('settings.gameAccounts.ownerOnlyNote')}
        </p>
      ) : null}

      {error ? (
        <p role="alert" className="mb-3 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {/* Phone: a card per account — name, the PC it is signed in on, the
          audited secret reveals, and the same actions. */}
      <div className="space-y-2 md:hidden">
        {loading && accounts.length === 0 ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : accounts.length === 0 ? (
          <p className="text-sm text-content-muted">{t('common.noResults')}</p>
        ) : (
          accounts.map((account) => (
            <div key={account.id} className="rounded-card border border-subtle bg-raised p-3">
              <div className="flex items-center gap-3">
                {controls.canManage ? (
                  <input
                    type="checkbox"
                    checked={selected.has(account.id)}
                    onChange={(event) => toggleSelected(account.id, event.target.checked)}
                    aria-label={t('gameAccountTools.selectAccount', { name: account.accountName })}
                    className="h-5 w-5 shrink-0 accent-[var(--brand-primary)]"
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-content">
                    {account.accountName}
                  </p>
                  <p className="truncate text-xs text-content-muted">
                    <span dir="ltr">{account.username}</span>
                    {' · '}
                    {account.company}
                    {' · '}
                    {t('settings.gameAccounts.games')}: {account.gameCount}
                  </p>
                  {account.email ? (
                    <p className="truncate text-xs text-content-muted" dir="ltr">
                      {account.email}
                    </p>
                  ) : null}
                  <p className="flex items-center gap-1 truncate text-xs text-content-secondary">
                    <Monitor className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span className="sr-only">{t('gameAccountTools.madeForPc')}: </span>
                    {renderPc(account)}
                  </p>
                </div>
                {controls.canManage ? (
                  <div className="flex shrink-0 items-center gap-1">
                    {controls.canReveal ? (
                      <>
                        {renderSecret(account, 'password', 'card')}
                        {renderSecret(account, 'emailPassword', 'card')}
                      </>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => setEditing(account)}
                      title={t('common.edit')}
                      aria-label={t('common.edit')}
                      className="grid h-10 w-10 place-items-center rounded-full text-content-secondary hover:bg-hover"
                    >
                      <Pencil className="h-4 w-4" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(account)}
                      title={t('common.delete')}
                      aria-label={t('common.delete')}
                      className="grid h-10 w-10 place-items-center rounded-full text-content-secondary hover:bg-hover hover:text-danger"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="overflow-x-auto rounded-card border border-subtle max-md:hidden">
        <table className="w-full min-w-[64rem] border-collapse text-sm">
          <thead>
            <tr className="bg-raised text-content-secondary">
              {controls.canManage ? (
                <th className="w-10 border-b border-subtle p-3 text-start font-medium">
                  <input
                    type="checkbox"
                    checked={allShownSelected}
                    onChange={(event) => selectAllShown(event.target.checked)}
                    aria-label={t('gameAccountTools.selectAllShown')}
                    className="h-4 w-4 accent-[var(--brand-primary)]"
                  />
                </th>
              ) : null}
              <th className="border-b border-subtle p-3 text-start font-medium">
                {t('settings.gameAccounts.accountName')}
              </th>
              <th className="border-b border-subtle p-3 text-start font-medium">
                {t('common.username')}
              </th>
              <th className="border-b border-subtle p-3 text-start font-medium">
                {t('settings.gameAccounts.email')}
              </th>
              <th className="border-b border-subtle p-3 text-start font-medium">
                {t('settings.gameAccounts.company')}
              </th>
              <th className="border-b border-subtle p-3 text-start font-medium">
                {t('settings.gameAccounts.games')}
              </th>
              <th className="border-b border-subtle p-3 text-start font-medium">
                {t('gameAccountTools.madeForPc')}
              </th>
              {controls.canManage ? (
                <>
                  <th className="border-b border-subtle p-3 text-start font-medium">
                    {t('settings.gameAccounts.secrets')}
                  </th>
                  <th className="border-b border-subtle p-3 text-start font-medium">
                    {t('common.edit')}
                  </th>
                  <th className="border-b border-subtle p-3 text-start font-medium">
                    {t('common.delete')}
                  </th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {loading && accounts.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="p-4 text-center text-content-muted">
                  {t('common.loading')}
                </td>
              </tr>
            ) : accounts.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="p-4 text-center text-content-muted">
                  {t('common.noResults')}
                </td>
              </tr>
            ) : (
              accounts.map((account, index) => (
                <tr
                  key={account.id}
                  className={index % 2 === 0 ? 'bg-[var(--row-even)]' : 'bg-[var(--row-odd)]'}
                >
                  {controls.canManage ? (
                    <td className="border-b border-subtle p-3">
                      <input
                        type="checkbox"
                        checked={selected.has(account.id)}
                        onChange={(event) => toggleSelected(account.id, event.target.checked)}
                        aria-label={t('gameAccountTools.selectAccount', {
                          name: account.accountName,
                        })}
                        className="h-4 w-4 accent-[var(--brand-primary)]"
                      />
                    </td>
                  ) : null}
                  <td className="border-b border-subtle p-3 text-content">{account.accountName}</td>
                  <td className="border-b border-subtle p-3 text-content-secondary" dir="ltr">
                    {account.username}
                  </td>
                  <td className="border-b border-subtle p-3 text-content-secondary" dir="ltr">
                    {account.email ?? <span className="text-content-muted">—</span>}
                  </td>
                  <td className="border-b border-subtle p-3 text-content-secondary">
                    {account.company}
                  </td>
                  <td className="border-b border-subtle p-3 tabular text-content-secondary">
                    {account.gameCount}
                  </td>
                  <td className="border-b border-subtle p-3 text-content-secondary" data-home-pc>
                    {renderPc(account)}
                  </td>
                  {controls.canManage ? (
                    <>
                      <td className="border-b border-subtle p-3">
                        {controls.canReveal ? (
                          <div className="flex flex-wrap items-center gap-2">
                            {renderSecret(account, 'password', 'row')}
                            {renderSecret(account, 'emailPassword', 'row')}
                          </div>
                        ) : null}
                      </td>
                      <td className="border-b border-subtle p-3">
                        <button
                          type="button"
                          onClick={() => setEditing(account)}
                          title={t('common.edit')}
                          aria-label={t('common.edit')}
                          className="grid h-8 w-8 place-items-center rounded-full text-content-secondary hover:bg-hover"
                        >
                          <Pencil className="h-4 w-4" aria-hidden />
                        </button>
                      </td>
                      <td className="border-b border-subtle p-3">
                        <button
                          type="button"
                          onClick={() => void handleDelete(account)}
                          title={t('common.delete')}
                          aria-label={t('common.delete')}
                          className="grid h-8 w-8 place-items-center rounded-full text-content-secondary hover:bg-hover hover:text-danger"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </button>
                      </td>
                    </>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {controls.canReveal ? (
        <p className="mt-2 text-xs text-content-muted">
          {t('settings.gameAccounts.revealAudited')}
        </p>
      ) : null}

      <div className="mt-6">
        <AccountVaultCard canSet={controls.canSetVaultPassword} />
      </div>

      {controls.canManage && creating ? (
        <GameAccountDrawer
          account={null}
          onClose={() => setCreating(false)}
          onSaved={() => void reload()}
        />
      ) : null}

      {controls.canManage && editing ? (
        <GameAccountDrawer
          account={editing}
          onClose={() => setEditing(null)}
          onSaved={() => void reload()}
        />
      ) : null}

      {controls.canManage && linking ? (
        <GameAccountsLinkDialog
          accounts={selectedAccounts}
          onClose={() => setLinking(false)}
          onDone={(updated) => afterBulk(t('gameAccountTools.link.done', { count: updated }))}
        />
      ) : null}

      {controls.canManage && assigning ? (
        <GameAccountsAssignDialog
          onClose={() => setAssigning(false)}
          onDone={(updated) => afterBulk(t('gameAccountTools.assign.done', { count: updated }))}
        />
      ) : null}
    </div>
  );
}
